/* eslint-disable max-lines */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import moment from 'moment-timezone';

import { DateRangeValue, DateRangeValueType } from '@recruitee/http-types';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { resolveWeekType } from '@recruitee/time';

import { ButtonComponent } from '../common/button/components/button/button.component';
import { IconButtonComponent } from '../common/button/components/icon-button/icon-button.component';
import { ButtonDropdownIndicatorDirective } from '../common/button/directives/button-dropdown/button-dropdown-indicator.directive';
import { DropdownHeaderComponent } from '../common/dropdown/dropdown-header/dropdown-header.component';
import { DropdownItemComponent } from '../common/dropdown/dropdown-item/dropdown-item.component';
import { DropdownComponent, DropdownWidth } from '../common/dropdown/dropdown.component';
import { IconColorDirective } from '../common/icon/directives/icon-color.directive';
import { IconComponent } from '../common/icon/icon.component';
import { TruncateComponent } from '../common/truncate/truncate.component';
import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
  OverlayPosition,
} from '../core/overlay/overlay-directives';
import {
  DatePickerComponent,
  DatePickerDirection,
} from '../forms/date-picker/date-picker.component';
import { InputComponent } from '../forms/input/input.component';
import { DateRangePickerLabelPipe, getDateRangePickerLabel } from './date-range-picker-label.pipe';

// eslint-disable-next-line max-lines-per-function
export function getDateRangeValueDates(
  value: DateRangeValue,
  timeZoneName: string | null = null,
): [number, number] {
  const weekType = resolveWeekType();

  switch (value.type) {
    case DateRangeValueType.Custom:
      return [
        moment.tz(value.min!, 'YYYY-MM-DD', timeZoneName!).startOf('day').unix(),
        moment.tz(value.max!, 'YYYY-MM-DD', timeZoneName!).endOf('day').unix(),
      ];
    case DateRangeValueType.Today:
      return [
        moment.tz(timeZoneName!).startOf('day').unix(),
        moment.tz(timeZoneName!).endOf('day').unix(),
      ];
    case DateRangeValueType.Yesterday:
      return [
        moment.tz(timeZoneName!).add(-1, 'day').startOf('day').unix(),
        moment.tz(timeZoneName!).add(-1, 'day').endOf('day').unix(),
      ];
    case DateRangeValueType.Tomorrow:
      return [
        moment.tz(timeZoneName!).add(1, 'day').startOf('day').unix(),
        moment.tz(timeZoneName!).add(1, 'day').endOf('day').unix(),
      ];
    case DateRangeValueType.ThisWeek:
      return [
        moment.tz(timeZoneName!).startOf(weekType).unix(),
        moment.tz(timeZoneName!).endOf(weekType).unix(),
      ];
    case DateRangeValueType.LastWeek:
      return [
        moment.tz(timeZoneName!).add(-1, 'week').startOf(weekType).unix(),
        moment.tz(timeZoneName!).add(-1, 'week').endOf(weekType).unix(),
      ];
    case DateRangeValueType.NextWeek:
      return [
        moment.tz(timeZoneName!).add(1, 'week').startOf(weekType).unix(),
        moment.tz(timeZoneName!).add(1, 'week').endOf(weekType).unix(),
      ];
    case DateRangeValueType.ThisMonth:
      return [
        moment.tz(timeZoneName!).startOf('month').unix(),
        moment.tz(timeZoneName!).endOf('month').unix(),
      ];
    case DateRangeValueType.LastMonth:
      return [
        moment.tz(timeZoneName!).add(-1, 'month').startOf('month').unix(),
        moment.tz(timeZoneName!).add(-1, 'month').endOf('month').unix(),
      ];
    case DateRangeValueType.ThisQuarter:
      return [
        moment.tz(timeZoneName!).startOf('quarter').unix(),
        moment.tz(timeZoneName!).endOf('quarter').unix(),
      ];
    case DateRangeValueType.LastQuarter:
      return [
        moment.tz(timeZoneName!).add(-1, 'quarter').startOf('quarter').unix(),
        moment.tz(timeZoneName!).add(-1, 'quarter').endOf('quarter').unix(),
      ];
    case DateRangeValueType.ThisYear:
      return [
        moment.tz(timeZoneName!).startOf('year').unix(),
        moment.tz(timeZoneName!).endOf('year').unix(),
      ];
    case DateRangeValueType.LastYear:
      return [
        moment.tz(timeZoneName!).add(-1, 'year').startOf('year').unix(),
        moment.tz(timeZoneName!).add(-1, 'year').endOf('year').unix(),
      ];
    case DateRangeValueType.Last7Days:
      return [
        moment.tz(timeZoneName!).add(-7, 'day').startOf('day').unix(),
        moment.tz(timeZoneName!).endOf('day').unix(),
      ];
    case DateRangeValueType.Last14Days:
      return [
        moment.tz(timeZoneName!).add(-14, 'day').startOf('day').unix(),
        moment.tz(timeZoneName!).endOf('day').unix(),
      ];
    case DateRangeValueType.Last20Days:
      return [
        moment.tz(timeZoneName!).add(-20, 'day').startOf('day').unix(),
        moment.tz(timeZoneName!).endOf('day').unix(),
      ];
    case DateRangeValueType.Last30Days:
      return [
        moment.tz(timeZoneName!).add(-30, 'day').startOf('day').unix(),
        moment.tz(timeZoneName!).endOf('day').unix(),
      ];
    case DateRangeValueType.Last60Days:
      return [
        moment.tz(timeZoneName!).add(-60, 'day').startOf('day').unix(),
        moment.tz(timeZoneName!).endOf('day').unix(),
      ];
    case DateRangeValueType.Last90Days:
      return [
        moment.tz(timeZoneName!).add(-90, 'day').startOf('day').unix(),
        moment.tz(timeZoneName!).endOf('day').unix(),
      ];
    case DateRangeValueType.Last365Days:
      return [
        moment.tz(timeZoneName!).add(-365, 'day').startOf('day').unix(),
        moment.tz(timeZoneName!).endOf('day').unix(),
      ];
    default:
      throw new Error(`Unknown DateRangeValueType: ${value.type}`);
  }
}

@Component({
  selector: 'rt-date-range-picker',
  styleUrls: ['./date-range-picker.component.less'],
  templateUrl: 'date-range-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ButtonComponent,
    ButtonDropdownIndicatorDirective,
    ConnectedOverlayDirective,
    DatePickerComponent,
    DateRangePickerLabelPipe,
    DropdownComponent,
    DropdownHeaderComponent,
    DropdownItemComponent,
    IconButtonComponent,
    IconColorDirective,
    IconComponent,
    InputComponent,
    OverlayOriginDirective,
    TranslatePipe,
    TruncateComponent,
  ],
})
export class DateRangePickerComponent {
  public selectedValueLabel: string | null;
  public DropdownWidth = DropdownWidth;

  @Input() public variant: 'button' | 'button-default' | 'input' | 'input-rounded' = 'button';
  @Input() public dropdownPosition: OverlayPosition = OverlayPosition.BottomLeft;
  @Input() public addClearOption = false;
  @Input() public smallPadding = true;

  @Input() public value: DateRangeValue;
  @Input() public dropdownIconHasMargin: boolean = true;

  @Input() public placeholder: string = this.i18n.translate('app.ui.date_range_picker.placeholder');
  @Input() public label: string = this.i18n.translate('app.common.custom_date');

  @Input() public horizontalDropdown: boolean = true;
  @Input() public customDropdownWidth: number = 350;

  @Input()
  public possibleDateRanges: DateRangeValueType[] = [
    DateRangeValueType.Today,
    DateRangeValueType.Yesterday,
    DateRangeValueType.ThisWeek,
    DateRangeValueType.LastWeek,
    DateRangeValueType.ThisMonth,
    DateRangeValueType.LastMonth,
    DateRangeValueType.ThisQuarter,
    DateRangeValueType.LastQuarter,
    DateRangeValueType.ThisYear,
    DateRangeValueType.LastYear,
    DateRangeValueType.Last30Days,
    DateRangeValueType.Last60Days,
    DateRangeValueType.Custom,
  ];

  @Input() public tabIndex: number;
  @Input() public blackout: ((date: moment.Moment) => boolean) | null = null;

  @Input() public disabled: boolean = false;
  @Input() public readOnly: boolean = false;

  @Input() public customRangeDirection: DatePickerDirection = 'today-past';

  @Output() public valueChange: EventEmitter<DateRangeValue> = new EventEmitter();

  public rangeValue: string[] | null;

  public showCustomDateRangePicker = false;
  public tmpValue: DateRangeValue | null = null;
  public dropdownOpen = false;

  @ViewChild('dropdownOverlay', { static: true })
  private dropdownOverlay: ConnectedOverlayDirective;

  constructor(
    private i18n: I18nService,
    protected cdRef: ChangeDetectorRef,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      this.tmpValue = this.value;

      this.rangeValue = this.getDatePickerValue();
      this.selectedValueLabel = this.getSelectedValueLabel();
    }
  }

  public trackByPossibleDateRange(index: number, item: DateRangeValueType): DateRangeValueType {
    return item;
  }

  public toggleDropdown(state: boolean = !this.dropdownOpen): void {
    this.dropdownOpen = state;
    this.cdRef.detectChanges();
  }

  public handleGoBackClick(): void {
    this.showCustomDateRangePicker = false;
    this.cdRef.detectChanges();
  }

  public clear() {
    this.changeValue({
      type: null,
    });
  }

  public handleDateRangeTypeChange(type: DateRangeValueType): void {
    if (type === DateRangeValueType.Custom) {
      this.showCustomDateRangePicker = true;
      this.cdRef.detectChanges();
    } else {
      this.changeValue({ type });
    }
  }

  public handleCustomDateRangeChange(dates: string[]): void {
    const nextValue: DateRangeValue = {
      type: DateRangeValueType.Custom,
      min: dates && dates.length >= 1 ? dates[0] : null,
      max: dates && dates.length >= 2 ? dates[1] : null,
    };
    if (nextValue.min && nextValue.max) {
      this.showCustomDateRangePicker = false;
      this.changeValue(nextValue);
    } else {
      this.tmpValue = nextValue;
    }

    this.rangeValue = this.getDatePickerValue();
    this.selectedValueLabel = this.getSelectedValueLabel();
    this.cdRef.detectChanges();
  }

  private changeValue(value: DateRangeValue) {
    this.valueChange.emit(value);
    this.toggleDropdown(false);
  }

  private getDatePickerValueForDateRangeValue(value: DateRangeValue) {
    return [value.min!, value.max!].filter(Boolean);
  }

  private getDatePickerValue(): string[] | null {
    return this.tmpValue ? this.getDatePickerValueForDateRangeValue(this.tmpValue) : null;
  }

  private getSelectedValueLabel(): string | null {
    return this.value?.type ? getDateRangePickerLabel(this.value, this.i18n) : null;
  }
}
